import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { REPORT_PERIOD_LIMIT_DAYS, type ReportPeriod, type RevenueReportView } from '@wodi/shared';
import { Button, PageHeader, PrintTable } from '@wodi/ui';
import { useState } from 'react';

import { TableScroll } from '../layout/screen.tsx';
import { fetchRevenue, reportKeys } from '../api/reports.ts';
import { sessionQuery } from '../api/session.ts';
import { money } from '../billing/format.ts';
import { CATEGORY_LABELS, plural } from '../reports/labels.ts';
import {
  AsAt,
  BackToReports,
  facilityWords,
  FacilityFilter,
  PeriodFields,
  periodIsValid,
  periodWords,
  PrintableReport,
  ReportActions,
  ReportStates,
  useFacilityChoice,
  useSheet,
  yesterdayIso,
} from '../reports/report-frame.tsx';
import styles from '../reports/reports.module.css';

type Facility = RevenueReportView['facilities'][number];

/**
 * Revenue by department and payer (plan §2.6; P2, P3): billed and collected side by side,
 * waivers, discounts and credit notes on their own lines, charges with no price counted
 * and never valued at zero.
 */
export function RevenueReportPage({ day }: { day?: string | undefined }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const choice = useFacilityChoice('revenue');
  const start = day ?? yesterdayIso();
  const [period, setPeriod] = useState<ReportPeriod>({ from: start, to: start });
  const [facility, setFacility] = useState(session?.facility.id ?? '');
  const [shown, setShown] = useState({ period, facilityIds: choice.idsFor(facility) });
  const report = useQuery({
    queryKey: reportKeys.report('revenue', shown),
    queryFn: () => fetchRevenue(shown),
  });
  const sheet = useSheet();
  const valid = periodIsValid(period, REPORT_PERIOD_LIMIT_DAYS.money);
  const data = report.data;
  return (
    <div className={styles.page}>
      <BackToReports />
      <PageHeader title="Revenue by department and payer" />
      <form
        className={styles.filters}
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) setShown({ period, facilityIds: choice.idsFor(facility) });
        }}
      >
        <PeriodFields
          period={period}
          onChange={setPeriod}
          maxDays={REPORT_PERIOD_LIMIT_DAYS.money}
        />
        <FacilityFilter reportKey="revenue" value={facility} onChange={setFacility} />
        <div>
          <Button type="submit" variant="primary" isDisabled={!valid}>
            Show report
          </Button>
        </div>
      </form>
      <ReportStates
        query={report}
        what="revenue"
        isEmpty={(d) =>
          d.facilities.every((f) => f.byCategory.length === 0 && f.collected.cents === '0')
        }
        emptyText={`No charges or payments on ${periodWords(shown.period)}.`}
      >
        {(d) => (
          <>
            <AsAt dataAsAt={d.dataAsAt} />
            {d.facilities.map((f) => (
              <FacilityRevenue key={f.facilityId} facility={f} several={d.facilities.length > 1} />
            ))}
          </>
        )}
      </ReportStates>
      {data === undefined ? null : (
        <>
          <ReportActions
            reportKey="revenue"
            sheet={sheet}
            printBody={{
              period: data.period,
              facilityIds: data.facilities.map((f) => f.facilityId),
              rowCount: data.facilities.reduce(
                (n, f) => n + f.byCategory.length + f.byPayer.length,
                0,
              ),
            }}
            exportBody={shown}
            disabled={report.isFetching}
          />
          <PrintableReport
            sheet={sheet}
            title="Revenue by department and payer"
            facility={facilityWords(data.facilities.map((f) => f.name))}
            period={periodWords(data.period)}
            dataAsAt={data.dataAsAt}
          >
            {data.facilities.map((f) => (
              <div key={f.facilityId}>
                {data.facilities.length > 1 ? <h2>{f.name}</h2> : null}
                <p>{headline(f)}</p>
                <PrintTable>
                  <DepartmentRows facility={f} />
                </PrintTable>
                <PrintTable>
                  <PayerRows facility={f} />
                </PrintTable>
              </div>
            ))}
          </PrintableReport>
        </>
      )}
    </div>
  );
}

function headline(f: Facility): string {
  const parts = [
    `Billed ${money(f.billed)}`,
    `waived ${money(f.waivers)}`,
    `discounted ${money(f.discounts)}`,
    `credit notes ${money(f.creditNotes)}`,
    `net ${money(f.net)}`,
    `collected ${money(f.collected)}.`,
  ];
  const unpriced =
    f.unpricedCount > 0
      ? ` ${plural(f.unpricedCount, 'charge has', 'charges have')} no price yet.`
      : '';
  return `${parts.join(', ')}${unpriced}`;
}

function DepartmentRows({ facility: f }: { facility: Facility }) {
  return (
    <>
      <thead>
        <tr>
          <th scope="col">Department</th>
          <th scope="col" data-numeric>
            Charges
          </th>
          <th scope="col" data-numeric>
            Billed
          </th>
          <th scope="col" data-numeric>
            Payer share
          </th>
          <th scope="col" data-numeric>
            Patient share
          </th>
        </tr>
      </thead>
      <tbody>
        {f.byCategory.map((line) => (
          <tr key={line.category}>
            <td>
              {CATEGORY_LABELS[line.category]}
              {line.unpricedCount > 0 ? (
                <span
                  className={styles.secondLine}
                >{`${String(line.unpricedCount)} not priced`}</span>
              ) : null}
            </td>
            <td data-numeric>{line.chargeCount}</td>
            <td data-numeric>{money(line.billed)}</td>
            <td data-numeric>{money(line.payerShare)}</td>
            <td data-numeric>{money(line.patientShare)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          <td data-numeric>{f.byCategory.reduce((n, l) => n + l.chargeCount, 0)}</td>
          <td data-numeric>{money(f.billed)}</td>
          <td />
          <td />
        </tr>
      </tfoot>
    </>
  );
}

function PayerRows({ facility: f }: { facility: Facility }) {
  return (
    <>
      <thead>
        <tr>
          <th scope="col">Payer</th>
          <th scope="col" data-numeric>
            Charges
          </th>
          <th scope="col" data-numeric>
            Billed
          </th>
          <th scope="col" data-numeric>
            Payer share
          </th>
          <th scope="col" data-numeric>
            Patient share
          </th>
        </tr>
      </thead>
      <tbody>
        {f.byPayer.map((line) => (
          <tr key={`${line.payerId}-${line.schemeId ?? ''}`}>
            <td>
              {line.payerName}
              {line.schemeName === null ? null : (
                <span className={styles.secondLine}>{line.schemeName}</span>
              )}
            </td>
            <td data-numeric>{line.chargeCount}</td>
            <td data-numeric>{money(line.billed)}</td>
            <td data-numeric>{money(line.payerShare)}</td>
            <td data-numeric>{money(line.patientShare)}</td>
          </tr>
        ))}
      </tbody>
    </>
  );
}

function FacilityRevenue({ facility: f, several }: { facility: Facility; several: boolean }) {
  const id = `revenue-${f.facilityId}`;
  return (
    <section className={styles.section} aria-labelledby={id}>
      <h2 id={id} className={styles.sectionHeading}>
        {several ? f.name : 'Billed and collected'}
      </h2>
      <p className={styles.summary}>{headline(f)}</p>
      <TableScroll>
        <table className={styles.table} aria-label="By department">
          <DepartmentRows facility={f} />
        </table>
      </TableScroll>
      <TableScroll>
        <table className={styles.table} aria-label="By payer">
          <PayerRows facility={f} />
        </table>
      </TableScroll>
    </section>
  );
}
